import type { CSSProperties } from "react";
import { ActionMenu } from "../ActionMenu";
import { Button } from "../Button";
import { Chip } from "../Chip";
import type { IconName } from "../Icon";
import type { Filter } from "./FilterChips";
import { ResultsCount } from "./ResultsCount";
import { SearchControl } from "./SearchControl";
import type { SortOption } from "./SortControl";
import { SortControl } from "./SortControl";

export interface DataControlsProps {
  /** Search configuration */
  search?: {
    value: string;
    onChange: (value: string) => void;
    onClear?: () => void;
    placeholder?: string;
    isLoading?: boolean;
  };
  /** @deprecated Search is always expanded on narrow containers. This prop is kept for backward compatibility but has no effect. */
  searchMobileMode?: "expanded" | "collapsed";

  /** Active filters */
  filters?: Filter[];
  /** Callback when a filter is removed */
  onRemoveFilter?: (id: string) => void;
  /** Callback to export data */
  exportControl?: {
    onClick: () => void;
    label: string;
    isDisabled?: boolean;
    /**
     * Spinner on the button while the export runs. Draining a large result set takes
     * seconds, and without this the click has no visible effect.
     */
    isLoading?: boolean;
    /** Defaults to "Upload". Override where a product uses a different export glyph. */
    icon?: IconName;
  };
  /** Callback to clear all filters */
  onClearAllFilters?: () => void;
  /** Callback to open filter management UI (drawer/modal) */
  onManageFilters?: () => void;
  /** Maximum visible filter chips before "+ X more" */
  maxVisibleFilterChips?: number;

  /** Sort configuration */
  sort?: {
    value: string;
    options: SortOption[];
    onChange: (value: string) => void;
    showLabel?: boolean;
    direction?: "asc" | "desc";
    onDirectionChange?: (direction: "asc" | "desc") => void;
  };

  /** Results count configuration */
  resultsCount?: {
    /** `null` renders `unknownLabel` (default em-dash) rather than a misleading 0. */
    count: number | null;
    label?: string;
    /** Appended to the number, e.g. "+" when the total is a lower bound. */
    suffix?: string;
    /** Shown when `count` is null. Defaults to an em-dash. */
    unknownLabel?: string;
    isLoading?: boolean;
    /** `data-testid` for the rendered count. */
    testId?: string;
  };

  /** Custom controls to render on the left side, before results count (e.g., saved views dropdown) */
  leftControls?: React.ReactNode;

  /** Callback to open column management UI (dialog/drawer) */
  onManageColumns?: () => void;

  /**
   * @deprecated Use `onManageColumns` instead. This prop will be removed in a future version.
   * Column picker control - rendered on the LEFT side with Sort (display controls)
   */
  columnPicker?: React.ReactNode;

  /** Custom controls to render on the right side (desktop) or below search (mobile) */
  customControls?: React.ReactNode;

  /** Action buttons (exposed on desktop, ActionMenu on mobile) */
  actions?: {
    items: Array<{
      id: string | number;
      label: string;
      icon?: IconName;
      onClick: () => void;
      variant?: "primary" | "secondary" | "destructive";
      disabled?: boolean;
    }>;
    /** Label for mobile menu button (accessibility) */
    menuLabel?: string;
  };

  /** Make controls sticky */
  sticky?: boolean;
  /** Sticky top offset - useful when stacking below sticky header (default: "0") */
  stickyTop?: string;
  /** Z-index for sticky positioning (default: 10) */
  zIndex?: number;

  /**
   * Controls the interaction style for sort in narrow containers.
   *
   * - `"auto"` (default): Narrow containers use compact sort (icon button → tray).
   * - `"desktop"`: Use inline Select dropdown for sort even in narrow containers.
   *   Useful when DataControls lives in a narrow panel on a desktop viewport
   *   (e.g., a side panel or split pane aside).
   */
  interactionMode?: "auto" | "desktop";

  /** Additional CSS classes */
  className?: string;

  /** Inline styles */
  style?: CSSProperties;
}

/**
 * DataControls
 *
 * A unified control bar for data display components (Lists, DataTables).
 * Provides search, filtering, sorting, results count, and action controls
 * with responsive layout.
 *
 * All data operations (search, filter, sort) are handled server-side.
 * This component is purely presentational and controlled.
 *
 * **Features:**
 * - Optional sticky positioning for data-heavy pages
 * - Works with PageLayout sticky headers
 * - Configurable z-index and offset
 *
 * **Responsive Behavior:**
 * - Narrow containers (< 640px): Two rows - Row 1: inputs (search + filter icon), Row 2: outputs (results + sort + actions)
 * - Wide containers (≥ 640px): Single row with all controls visible
 *
 * Example usage:
 * ```tsx
 * <DataControls
 *   resultsCount={{ count: 23, label: "sites" }}
 *   search={{
 *     value: searchQuery,
 *     onChange: setSearchQuery,
 *     onClear: () => setSearchQuery(''),
 *     placeholder: "Search sites..."
 *   }}
 *   filters={activeFilters}
 *   onRemoveFilter={removeFilter}
 *   exportControl={exportData}
 *   onClearAllFilters={clearAllFilters}
 *   onManageFilters={() => setFilterDrawerOpen(true)}
 *   sort={{
 *     value: sortBy,
 *     options: sortOptions,
 *     onChange: setSortBy
 *   }}
 * />
 * ```
 */
export function DataControls({
  search,
  searchMobileMode: _searchMobileMode = "expanded", // Deprecated: kept for backward compatibility
  filters = [],
  onRemoveFilter,
  onClearAllFilters,
  onManageFilters,
  exportControl,
  maxVisibleFilterChips = 3,
  sort,
  resultsCount,
  leftControls,
  onManageColumns,
  columnPicker,
  customControls,
  actions,
  interactionMode = "auto",
  sticky,
  stickyTop = "0",
  zIndex = 10,
  className = "",
  style,
}: DataControlsProps) {
  const hasFilters = filters.length > 0;
  const hasControls =
    search ||
    hasFilters ||
    onManageFilters ||
    sort ||
    resultsCount ||
    leftControls ||
    onManageColumns ||
    columnPicker ||
    customControls ||
    actions ||
    exportControl;

  // Don't render anything if no controls are provided
  if (!hasControls) {
    return null;
  }

  const stickyClasses = sticky ? "sticky bg-background-surface" : "";
  const stickyStyles = sticky ? { top: stickyTop, zIndex } : {};

  return (
    <div
      className={`@container flex flex-col gap-3 bg-background-surface py-3 border-b border-border-default ${stickyClasses} ${className}`}
      style={{ ...style, ...stickyStyles }}
    >
      {/* Wide container: Single row with left/right layout */}
      <div className="hidden @[640px]:flex @[640px]:items-center @[640px]:justify-between @[640px]:gap-4">
        {/* LEFT SIDE: Output/Status (what you're viewing) */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {/* Left controls (e.g., saved views dropdown) */}
          {leftControls}

          {/* Divider between left controls and results */}
          {leftControls && resultsCount && <div className="h-4 w-px bg-border-default" />}

          {/* Results count */}
          {resultsCount && (
            <ResultsCount
              count={resultsCount.count}
              label={resultsCount.label}
              suffix={resultsCount.suffix}
              unknownLabel={resultsCount.unknownLabel}
              isLoading={resultsCount.isLoading}
              testId={resultsCount.testId}
            />
          )}

          {/* Divider between results and sort */}
          {resultsCount && sort && <div className="h-4 w-px bg-border-default" />}

          {/* Sort */}
          {sort && (
            <SortControl
              value={sort.value}
              options={sort.options}
              onChange={sort.onChange}
              showLabel={sort.showLabel}
              direction={sort.direction}
              onDirectionChange={sort.onDirectionChange}
            />
          )}

          {/* Column picker */}
          {onManageColumns ? (
            <Button variant="secondary" size="sm" icon="Columns" onClick={onManageColumns}>
              Columns
            </Button>
          ) : (
            columnPicker
          )}

          {/* Filter chips (if active) */}
          {hasFilters && onRemoveFilter && (
            <>
              {filters.slice(0, maxVisibleFilterChips).map((filter) => (
                <Chip
                  key={filter.id}
                  variant="primary"
                  size="sm"
                  onRemove={() => onRemoveFilter(filter.id)}
                  isRemovable
                >
                  {filter.label}: {filter.value}
                </Chip>
              ))}
              {filters.length > maxVisibleFilterChips && onManageFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onManageFilters}
                  className="text-text-secondary hover:text-text-primary whitespace-nowrap"
                >
                  +{filters.length - maxVisibleFilterChips} more
                </Button>
              )}
              {onClearAllFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClearAllFilters}
                  className="text-text-secondary hover:text-text-primary whitespace-nowrap"
                >
                  Clear all
                </Button>
              )}
            </>
          )}
        </div>

        {/* RIGHT SIDE: Input/Actions (how to change it) */}
        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Custom controls (if provided, they replace the default controls) */}
          {customControls ? (
            customControls
          ) : (
            <>
              {/* Actions - exposed buttons on desktop */}
              {actions && actions.items.length > 0 && (
                <>
                  {actions.items.map((action) => (
                    <Button
                      key={action.id}
                      variant={action.variant || "secondary"}
                      size="sm"
                      icon={action.icon}
                      onPress={action.onClick}
                      isDisabled={action.disabled}
                    >
                      {action.label}
                    </Button>
                  ))}
                </>
              )}

              {/* Search */}
              {search && (
                <div className="w-64">
                  <SearchControl
                    value={search.value}
                    onChange={search.onChange}
                    onClear={search.onClear}
                    placeholder={search.placeholder}
                    isLoading={search.isLoading}
                  />
                </div>
              )}

              {/* Filters button */}
              {onManageFilters && (
                <Button
                  variant="secondary"
                  size="sm"
                  icon="Sliders"
                  badgeNumber={filters.length > 0 ? filters.length : undefined}
                  onClick={onManageFilters}
                >
                  Filters
                </Button>
              )}

              {/* Export button */}
              {exportControl && (
                <Button
                  variant="secondary"
                  size="sm"
                  icon={exportControl.icon ?? "Upload"}
                  onClick={exportControl.onClick}
                  isDisabled={exportControl.isDisabled}
                  isLoading={exportControl.isLoading}
                >
                  {exportControl.label}
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Narrow container: Two-row layout */}
      <div className="@[640px]:hidden">
        {/* Row 1: Actions menu, search/custom controls, and filter button */}
        <div className="flex flex-col gap-3 mb-3">
          {/* Actions + Search/Custom controls */}
          <div className="flex items-center gap-3">
            {customControls ? (
              <div className="flex-1">{customControls}</div>
            ) : (
              <>
                {/* Actions - always ActionMenu in narrow containers */}
                {actions && actions.items.length > 0 && (
                  <ActionMenu
                    items={actions.items.map((action) => ({
                      id: action.id,
                      name: action.label,
                      iconName: action.icon,
                      variant: action.variant === "destructive" ? "destructive" : "primary",
                      disabled: action.disabled,
                      onAction: action.onClick,
                    }))}
                    align="left"
                    size="sm"
                  >
                    <Button
                      variant="secondary"
                      size="sm"
                      icon="DotsThreeVertical"
                      aria-label={actions.menuLabel || "Actions"}
                    />
                  </ActionMenu>
                )}

                {/* Search - always expanded on narrow layouts */}
                {search && (
                  <div className="flex-1">
                    <SearchControl
                      value={search.value}
                      onChange={search.onChange}
                      onClear={search.onClear}
                      placeholder={search.placeholder}
                      isLoading={search.isLoading}
                    />
                  </div>
                )}

                {/* Filter icon button */}
                {onManageFilters && (
                  <Button
                    variant="secondary"
                    size="sm"
                    icon="Sliders"
                    badgeNumber={filters.length > 0 ? filters.length : undefined}
                    onClick={onManageFilters}
                    aria-label={`Filters${filters.length > 0 ? ` (${filters.length} active)` : ""}`}
                  />
                )}

                {/* Export button */}
                {exportControl && (
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={exportControl.icon ?? "Upload"}
                    onClick={exportControl.onClick}
                    isDisabled={exportControl.isDisabled}
                    isLoading={exportControl.isLoading}
                  >
                    {exportControl.label}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Row 2: Left controls, Results and sort */}
        <div className="flex items-center justify-between gap-3">
          {/* Left: Left controls and Results */}
          <div className="flex items-center gap-3">
            {leftControls}
            {/* No `testId` on this copy: the wide and narrow layouts are both in the
                DOM, hidden by container queries, so duplicating the hook would break a
                `getByTestId`. The wide copy above carries it. */}
            {resultsCount && (
              <ResultsCount
                count={resultsCount.count}
                label={resultsCount.label}
                suffix={resultsCount.suffix}
                unknownLabel={resultsCount.unknownLabel}
                isLoading={resultsCount.isLoading}
              />
            )}
          </div>

          {/* Right: Sort */}
          <div className="flex items-center gap-2">
            {sort && (
              <SortControl
                value={sort.value}
                options={sort.options}
                onChange={sort.onChange}
                showLabel={false}
                direction={sort.direction}
                onDirectionChange={sort.onDirectionChange}
                compact={interactionMode !== "desktop"}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
